import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import { useQcCustomerDetail, useSaveQcCustomer, useSubmitQcCustomer } from '@/libs/fleet-api/services/qc-customer';
import { QC_CUSTOMER_TOP_FIELDS, QC_NEW_CUSTOMER_SECTIONS } from '@/pages/robotic/components/QCAccordionForm';
import QCTabForm from '@/pages/robotic/components/QCTabForm';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Form, notification, Spin } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import { mapDetailToFormValues, mapFormValuesToSaveRequest, applyFieldMeta, applySectionsMeta } from './qc-customer-mapping';
import { QCPraPencairanContext, type QCPraPencairanContextValue } from '@/pages/robotic/qc-pra-pencairan/QCPraPencairanContext';
import type { FieldMetaMap as PraFieldMetaMap } from '@/pages/robotic/qc-pra-pencairan/qc-pd-mapping';

type QCNewCustomerPageProps = {
	customerId: string;
	mode: 'edit' | 'view';
	onBack?: () => void;
};

const QCNewCustomerPage: FC<QCNewCustomerPageProps> = ({ customerId, mode, onBack }) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();
	const { form } = useForm();
	const [hasSavedDraft, setHasSavedDraft] = useState(false);

	const detailQuery = useQcCustomerDetail(customerId);
	const detail = detailQuery.data?.data;
	const isLoading = detailQuery.isLoading;

	const mapped = useMemo(() => {
		if (!detail) return null;
		return mapDetailToFormValues(detail);
	}, [detail]);

	const [sahamState, setSahamState] = useState<{ data: Record<string, unknown>[]; meta: PraFieldMetaMap[] }>({ data: [], meta: [] });

	useEffect(() => {
		if (mapped) {
			form.setFieldsValue(mapped.values);
			const data = (mapped.values.sahamData as Record<string, unknown>[]) ?? [];
			const meta = (mapped.values.sahamDataMeta as PraFieldMetaMap[]) ?? [];
			setSahamState({ data, meta });
		}
	}, [mapped, form]);

	const fieldMeta = useMemo(() => mapped?.fieldMeta ?? {}, [mapped]);

	const contextValue: QCPraPencairanContextValue = useMemo(() => ({
		fieldMeta,
		saham: sahamState,
		updateSahamRow: (index, row) => {
			setSahamState((prev) => {
				const nextData = [...prev.data];
				nextData[index] = row;
				return { ...prev, data: nextData };
			});
			const formData = (form.getFieldValue('sahamData') as Record<string, unknown>[] | undefined) ?? [];
			const nextFormData = [...formData];
			nextFormData[index] = row;
			form.setFieldValue('sahamData', nextFormData);
		},
		asset: {
			assetMeta: null,
			customerAddress: { alamat: '', rt: '', rw: '', kodePos: '', kelurahan: '', kecamatan: '', kota: '' },
		},
		insurance: { paymentSchedule: [], tenorYears: [], paymentDocumentUrl: '', policyDocumentUrl: '' },
		incentive: { distributionData: [], showroomEmployeeOptions: {}, mandiriEmployeeOptions: {}, documentUploadUrl: '' },
	}), [fieldMeta, sahamState, form]);

	const topFields = useMemo(() => applyFieldMeta(QC_CUSTOMER_TOP_FIELDS, fieldMeta), [fieldMeta]);
	const sections = useMemo(() => applySectionsMeta(QC_NEW_CUSTOMER_SECTIONS, fieldMeta), [fieldMeta]);

	const saveMutation = useSaveQcCustomer();
	const submitMutation = useSubmitQcCustomer();

	const isReadOnly = mode === 'view';
	const isSaving = saveMutation.isPending;
	const isSubmitting = submitMutation.isPending;

	const handleBack = () => {
		if (typeof onBack === 'function') {
			onBack();
		} else {
			navigate('QCNewCustomerListPage');
		}
	};

	const handleSaveDraft = async () => {
		const values = form.getFieldsValue() as Record<string, unknown>;
		const payload = mapFormValuesToSaveRequest(customerId, values, 1);

		saveMutation.mutate(payload, {
			onSuccess: () => {
				setHasSavedDraft(true);
				notification.success({
					message: 'Draft Tersimpan',
					description: 'Data berhasil disimpan sebagai draft.',
				});
			},
		});
	};

	const handleSubmit = async () => {
		try {
			await form.validateFields();
		} catch {
			return;
		}

		const values = form.getFieldsValue() as Record<string, unknown>;
		const payload = mapFormValuesToSaveRequest(customerId, values, 2);

		submitMutation.mutate(payload, {
			onSuccess: () => {
				notification.success({
					message: 'Berhasil Disubmit',
					description: 'Data QC New Customer berhasil disubmit.',
				});
				handleBack();
			},
		});
	};

	if (isLoading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
				<Spin size='large' />
			</div>
		);
	}

	const mainInfo = detail?.mainInformation;

	const infoItems: DataRowItem[] = [
		[
			['Nama Customer', mainInfo?.customerName ? (
				<a href='#' onClick={(e) => { e.preventDefault(); openCustomerDetail(customerId); }}>
					{mainInfo.customerName}
				</a>
			) : '-'],
			['Customer ID', customerId ? (
				<a href='#' onClick={(e) => { e.preventDefault(); openCustomerDetail(customerId); }}>
					{customerId}
				</a>
			) : '-'],
		],
		[
			['NPWP', mainInfo?.npwpNo || '-'],
			['Group BMPP', mainInfo?.groupBmppName ? (
				<a href='#' onClick={(e) => { e.preventDefault(); if (mainInfo.groupBmppId) openCustomerGroupDetail(mainInfo.groupBmppId); }}>
					{mainInfo.groupBmppName}
				</a>
			) : '-'],
		],
	];

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			{/* Header */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
				<Button
					onClick={handleBack}
					icon={<ArrowLeftOutlined />}
					style={{ width: 44, height: 44, background: '#1557BC', color: '#fff' }}
				/>
				<FleetTitle marginBottom={0} style={{ flex: 1 }}>
					Main Information
				</FleetTitle>
			</div>

			{/* Main Information */}
			<DataRow items={infoItems} loading={isLoading} />

			{/* Main Data */}
			<FleetTitle level={2} marginBottom={0}>
				Data Utama
			</FleetTitle>

			<QCPraPencairanContext.Provider value={contextValue}>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
				>
					<QCTabForm
						tabs={[
							{
								key: 'data-customer',
								label: 'Data Customer',
								sections,
								topFields,
							},
						]}
						form={form}
						readOnly={isReadOnly}
					/>
				</Form>
			</QCPraPencairanContext.Provider>

			{/* Footer Buttons */}
			<div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 30 }}>
				<Button onClick={handleBack}>
					Kembali
				</Button>

				{mode === 'edit' && (
					<>
						<Button
							type='primary'
							loading={isSaving}
							disabled={isSubmitting}
							onClick={handleSaveDraft}
						>
							Save Draft
						</Button>
						<Button
							type='primary'
							loading={isSubmitting}
							disabled={!hasSavedDraft || isSaving}
							onClick={handleSubmit}
						>
							Submit
						</Button>
					</>
				)}
			</div>
		</div>
	);
};

export default QCNewCustomerPage;
